import axios from 'axios'
import AuthService from './auth-header'

let API_URL = ''
if (process.env.NODE_ENV === "production") {
  API_URL = 'https://ats.sattvahuman.in/api/'
} else {
  API_URL = 'http://192.168.0.30:5000/api/'
}

class PunchAttendanceService {
  /**
   * Create a new punch attendance record (Punch In)
   * @param {Object} data - Punch attendance data
   * @param {string} data.emp_id - Employee ID
   * @param {string} data.punch_in_time - ISO timestamp
   * @param {number} data.latitude - Latitude
   * @param {number} data.longitude - Longitude
   * @param {number} data.accuracy - Location accuracy in meters
   * @param {Object} data.device_info - Device information
   * @param {Object} data.location_validation - Location validation data
   * @returns {Promise<Object>} API response
   */
  async punchIn(data) {
    if (!data.emp_id) return { status: 'FAILED', message: 'Employee ID is required' }
    if (data.latitude == null || data.longitude == null) return { status: 'FAILED', message: 'Location is required' }
    try {
      const res = await axios.post(API_URL + 'punch-attendance/punch-in', data, {
        headers: AuthService.authHeader()
      })
      return res.data
    } catch (err) {
      console.error('Error in punch in:', err)
      return err.response?.data || { status: 'FAILED', message: err.message }
    }
  }

  /**
   * Record punch out (finds today's record and updates it, or creates new if not found)
   * @param {Object} data - Punch out data
   * @param {string} data.emp_id - Employee ID
   * @param {string} data.punch_out_time - ISO timestamp
   * @param {number} data.latitude - Latitude
   * @param {number} data.longitude - Longitude
   * @param {number} data.accuracy - Location accuracy in meters
   * @param {Object} data.device_info - Device information
   * @param {Object} data.location_validation - Location validation data
   * @returns {Promise<Object>} API response
   */
  async punchOut(data) {
    if (!data.emp_id) return { status: 'FAILED', message: 'Employee ID is required' }
    if (data.latitude == null || data.longitude == null) return { status: 'FAILED', message: 'Location is required' }
    try {
      const res = await axios.post(API_URL + 'punch-attendance/punch-out', data, {
        headers: AuthService.authHeader()
      })
      return res.data
    } catch (err) {
      console.error('Error in punch out:', err)
      return err.response?.data || { status: 'FAILED', message: err.message }
    }
  }

  /**
   * Get today's punch attendance for an employee
   * @param {string} emp_id - Employee ID
   * @returns {Promise<Object>} API response with today's punch record
   */
  async getTodayPunch(emp_id) {
    try {
      const toISTDate = (d) => d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)

      const res = await axios.get(API_URL + 'punch-attendance/employee/' + emp_id, {
        params: {
          start_date: toISTDate(today),
          end_date: toISTDate(tomorrow)
        },
        headers: AuthService.authHeader()
      })
      return res.data
    } catch (err) {
      console.error('Error getting today punch:', err)
      return err.response?.data || { status: 'FAILED', message: err.message, data: [] }
    }
  }

  /**
   * Get all punch attendances for an employee
   * @param {string} emp_id - Employee ID
   * @param {string} start_date - Optional start date (ISO format)
   * @param {string} end_date - Optional end date (ISO format)
   * @returns {Promise<Object>} API response
   */
  async getEmployeePunches(emp_id, start_date = null, end_date = null) {
    try {
      const params = {}
      if (start_date) params.start_date = start_date
      if (end_date) params.end_date = end_date

      const res = await axios.get(API_URL + 'punch-attendance/employee/' + emp_id, {
        params,
        headers: AuthService.authHeader()
      })
      return res.data
    } catch (err) {
      console.error('Error getting employee punches:', err)
      return err.response?.data || { status: 'FAILED', message: err.message, data: [] }
    }
  }

  /**
   * Get a single punch attendance by ID
   * @param {number} id - Punch attendance record ID
   * @returns {Promise<Object>} API response
   */
  async getPunchById(id) {
    try {
      const res = await axios.get(API_URL + 'punch-attendance/' + id, {
        headers: AuthService.authHeader()
      })
      return res.data
    } catch (err) {
      console.error('Error getting punch by id:', err)
      return err.response?.data || { status: 'FAILED', message: err.message }
    }
  }

  /**
   * Get all punch attendances (with optional filters)
   * @param {Object} params - Query parameters
   * @param {string} params.emp_id - Optional employee ID filter
   * @param {string} params.start_date - Optional start date filter
   * @param {string} params.end_date - Optional end date filter
   * @returns {Promise<Object>} API response
   */
  async getAllPunches(params = {}) {
    try {
      const res = await axios.get(API_URL + 'punch-attendance', {
        params,
        headers: AuthService.authHeader()
      })
      return res.data
    } catch (err) {
      console.error('Error getting all punches:', err)
      return err.response?.data || { status: 'FAILED', message: err.message, data: [] }
    }
  }

  /**
   * Get punch config for an employee
   * @param {string} emp_id - Employee ID
   * @returns {Promise<Object>} API response with punch config
   */
  async getPunchConfig(emp_id) {
    try {
      const res = await axios.get(API_URL + 'punch-config/' + emp_id, {
        headers: AuthService.authHeader()
      })
      return res.data
    } catch (err) {
      console.error('Error getting punch config:', err)
      return err.response?.data || { status: 'FAILED', message: err.message }
    }
  }

  /**
   * Get leave type and status by date range for an employee
   * @param {string} emp_id - Employee ID
   * @param {string} from_date - Start date (YYYY-MM-DD)
   * @param {string} to_date - End date (YYYY-MM-DD)
   * @returns {Promise<Object>} API response with leave data
   */
  async getLeaveTypeAndStatusByDateRange(emp_id, from_date, to_date = null) {
    try {
      const params = { from_date }
      if (to_date) params.to_date = to_date
      
      const res = await axios.get(API_URL + 'leave/type-and-status/' + emp_id, {
        params,
        headers: AuthService.authHeader()
      })
      return res.data
    } catch (err) {
      console.error('Error getting leave type and status:', err)
      return err.response?.data || { status: 'FAILED', message: err.message, data: [] }
    }
  }
}

export default new PunchAttendanceService()
